<template>
  <div class="scan-visit" :class="`scan-visit--${tone}`">
    <div class="scan-visit__top">
      <span class="scan-visit__chip">
        <span v-if="variant === 'live'" class="scan-visit__dot" aria-hidden="true"></span>
        <i v-else class="fa-regular fa-triangle-exclamation" aria-hidden="true"></i>
        {{ variant === 'live' ? primaryLabel : 'Not signed out' }}
      </span>
      <span v-if="!visit.expired" class="scan-visit__since">
        {{ visit.earlierSeconds ? 'back in at' : 'since' }} {{ visit.signedInTime }}
      </span>
    </div>

    <div class="scan-visit__name">{{ visit.sessionName }}</div>

    <!-- Keys on same-tag v-if branches: FontAwesome swaps each <i> for an
         <svg> Vue does not track, so a reused element would keep the old icon
         (this row flips to "Ended" while the card is on screen). -->
    <div class="scan-visit__meta">
      <span v-if="variant === 'stale' && visit.ended && visit.sessionEndTime" key="ended">Ended {{ visit.sessionEndTime }}</span>
      <span v-else-if="visit.sessionTime" key="time">
        <i class="fa-regular fa-clock" aria-hidden="true"></i>
        {{ visit.sessionTime }}
      </span>
      <span v-if="visit.room" key="room">
        <i class="fa-regular fa-location-dot" aria-hidden="true"></i>
        {{ visit.room }}
      </span>
    </div>

    <template v-if="variant === 'live'">
      <div key="live-timer" class="scan-visit__timer">{{ elapsed }}</div>
      <div key="live-note" class="scan-visit__note">
        <template v-if="visit.earlierSeconds">Total in the room · this visit {{ thisVisit }}</template>
        <template v-else>Time in the room · keeps counting until you scan out</template>
      </div>
    </template>

    <template v-else-if="!visit.expired">
      <div key="stale-row" class="scan-visit__row">
        <div>
          <div class="scan-visit__timer scan-visit__timer--small">{{ elapsed }}</div>
          <div class="scan-visit__note">still counting</div>
        </div>

        <button type="button" class="scan-visit__sign-out" @click="$emit('sign-out', visit)">
          <i class="fa-regular fa-qrcode" aria-hidden="true"></i>
          Sign out
        </button>
      </div>

      <div key="stale-callout" class="scan-visit__callout">
        You didn't scan out when you left.
        <template v-if="visit.room">Scan the code at <b>{{ visit.room }}</b></template>
        <template v-else>Scan the session code</template>
        before <b>{{ visit.signOutUntilTime }}</b>, otherwise ask event staff to fix your time.
      </div>
    </template>

    <template v-else>
      <div key="expired-note" class="scan-visit__note scan-visit__note--strong">Signed in at {{ visit.signedInTime }}</div>
      <div key="expired-callout" class="scan-visit__callout">
        The sign-out window closed at <b>{{ visit.signOutUntilTime }}</b>. Ask event staff to fix your time.
      </div>
    </template>
  </div>
</template>

<script>
import { formatDuration } from './scan-clock';

export default {
  props: {
    // A visit from scan-clock's normalizeVisit (stale ones also carry `expired` / `ended`).
    visit: { type: Object, required: true },
    variant: { type: String, default: 'live' },
    // Server-corrected current time in ms.
    now: { type: Number, required: true },
    primaryLabel: { type: String, default: 'In the room' },
  },
  computed: {
    tone() {
      return this.variant === 'live' ? 'live' : 'warn';
    },

    // The session's time in the room: earlier visits plus the current one,
    // so signing back in picks the timer up instead of restarting it.
    elapsed() {
      return formatDuration((this.visit.earlierSeconds || 0) * 1000 + this.now - this.visit.signedInAt);
    },

    thisVisit() {
      return formatDuration(this.now - this.visit.signedInAt);
    },
  },
}
</script>

<style lang="scss" scoped>
.scan-visit {
  --tone: var(--scan-live);
  --tone-tint: var(--scan-live-tint);
  --tone-border: var(--scan-live-border);

  padding: 14px 16px;
  border: 1.5px solid var(--tone-border);
  border-radius: calc(var(--attendee-card-border-radius) + 4px);
  background-image: linear-gradient(160deg, var(--attendee-card-color) 0%, var(--tone-tint) 100%);
  color: var(--scan-ink);

  &--warn {
    --tone: var(--scan-warn);
    --tone-tint: var(--scan-warn-tint);
    --tone-border: var(--scan-warn-border);
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 9px;
    border-radius: 99px;
    background-color: color-mix(in srgb, var(--tone) 14%, transparent);
    color: var(--tone);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: currentColor;
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--tone) 25%, transparent);
  }

  &__since {
    color: var(--scan-muted);
    font-size: 12px;
    white-space: nowrap;
  }

  &__name {
    margin: 8px 0 2px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    color: var(--scan-muted);
    font-size: 12px;

    i {
      margin-right: 3px;
    }
  }

  &__timer {
    margin: 10px 0 2px;
    color: var(--tone);
    font-size: 40px;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum";

    &--small {
      font-size: 32px;
    }
  }

  &__note {
    color: var(--scan-muted);
    font-size: 12px;
    line-height: 1.45;

    &--strong {
      margin-top: 10px;
      color: var(--scan-ink);
      font-weight: 600;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__sign-out {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
    height: 44px;
    padding: 0 16px;
    border: 1.5px solid currentColor;
    border-radius: 12px;
    background-color: var(--attendee-card-color);
    color: var(--tone);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  &__callout {
    margin-top: 10px;
    padding: 9px 11px;
    border-radius: 12px;
    background-color: color-mix(in srgb, var(--tone) 10%, transparent);
    color: var(--tone);
    font-size: 12.5px;
    line-height: 1.45;
  }
}
</style>
